import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
  Popover,
  Typography,
  FormControlLabel,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as NotifyIcon,
  NotificationsOff as NotifyOffIcon,
} from '@mui/icons-material';
import { ClientInterest } from '../types/client';
import { ClientService } from '../services/clientService';

interface InterestNotifyControlsProps {
  interest: ClientInterest;
  onUpdated: () => void;
}

const InterestNotifyControls: React.FC<InterestNotifyControlsProps> = ({
  interest,
  onUpdated,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);

  const handleToggle = async (
    field: 'auto_notify' | 'notify_whatsapp' | 'notify_email' | 'notify_sms',
    value: boolean
  ) => {
    setSaving(true);
    try {
      await ClientService.updateInterestNotify(interest.id, { [field]: value });
      onUpdated();
    } catch (err) {
      console.error('Failed to update notify setting:', err);
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
      <Tooltip title={interest.auto_notify ? 'Auto-notify ON — click to configure' : 'Auto-notify OFF'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color={interest.auto_notify ? 'primary' : 'default'}
          sx={{ p: 0.25 }}
        >
          {interest.auto_notify ? (
            <NotifyIcon fontSize="small" />
          ) : (
            <NotifyOffIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      {/* Channel indicators */}
      {interest.auto_notify && (
        <>
          {interest.notify_whatsapp && (
            <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366', ml: 0.25 }} />
          )}
          {interest.notify_email && (
            <EmailIcon sx={{ fontSize: 14, color: '#1976d2', ml: 0.25 }} />
          )}
          {interest.notify_sms && (
            <SmsIcon sx={{ fontSize: 14, color: '#f57c00', ml: 0.25 }} />
          )}
        </>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Notification Settings
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={interest.auto_notify ?? true}
                onChange={(e) => handleToggle('auto_notify', e.target.checked)}
                disabled={saving}
                size="small"
              />
            }
            label="Auto-notify"
          />

          {interest.auto_notify && (
            <Box sx={{ pl: 2, mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={interest.notify_whatsapp ?? true}
                    onChange={(e) => handleToggle('notify_whatsapp', e.target.checked)}
                    disabled={saving}
                    size="small"
                    color="success"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
                    WhatsApp
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={interest.notify_email ?? true}
                    onChange={(e) => handleToggle('notify_email', e.target.checked)}
                    disabled={saving}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16 }} />
                    Email
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={interest.notify_sms ?? false}
                    onChange={(e) => handleToggle('notify_sms', e.target.checked)}
                    disabled={saving}
                    size="small"
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SmsIcon sx={{ fontSize: 16, color: '#f57c00' }} />
                    SMS
                  </Box>
                }
              />
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default InterestNotifyControls;
